<template>
  <v-card class="elevation-0">
    <v-toolbar flat color="white" v-if="title">
      <v-icon color="blue" class="pr-2">linear_scale</v-icon>
      <v-toolbar-title>{{ title }}</v-toolbar-title>
      <v-btn
        @click="reloadData"
        :disabled="loading"
        class="ml-auto"
        small
        outlined
      >
        <v-icon class="pr-2" small>refresh</v-icon>
        Refresh Table
      </v-btn>
    </v-toolbar>
    <v-data-table
      :headers="headers"
      :items="scheduled"
      :loading="loading"
      :item-key="type"
    >
      <template v-slot:item.dataSource="{ item }">
        <v-tooltip color="secondary" text-color="white" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              v-bind="attrs"
              v-on="on"
              color="secondary"
              text-color="white"
              light
              small
            >
              {{ item.dataSource }}
            </v-chip>
          </template>
          <p>Files in this tube require data in the following fields:</p>

          <strong>ID Fields</strong>
          <p v-if="!item.fields.uniqueIdFields.length">Randomly generated</p>
          <li v-for="uniqueIdField in item.fields.uniqueIdFields">
            {{ uniqueIdField }}
          </li>

          <br />
          <strong>Required Fields</strong>
          <p v-if="!item.fields.requiredFields.length">None</p>
          <li v-for="requiredField in item.fields.requiredFields">
            {{ requiredField }}
          </li>
          <br />

          <p>
            Files in this tube will be omitted of the following field data
            before loading:
          </p>

          <strong>Omitted Fields</strong>
          <p v-if="!item.fields.omitFields.length">None</p>
          <li v-for="omitField in item.fields.omitFields">
            {{ omitField }}
          </li>
        </v-tooltip>
      </template>

      <template v-slot:item.scheduledMessages="{ item }">
        <v-chip color="secondary" small dark outlined>
          {{ item.orcMessages }}
        </v-chip>
      </template>

      <template v-slot:item.triggeredMessages="{ item }">
        <v-chip color="secondary" small dark outlined>
          {{ item.triggeredMessages }}
        </v-chip>
      </template>

      <template v-slot:item.retMessages="{ item }">
        <v-chip color="secondary" small dark outlined>
          {{ item.retMessages }}
        </v-chip>
      </template>

      <template v-slot:item.endpoint="{ item }">
        <div
          class="
            align-center align-content-start
            d-flex
            flex-nowrap
            justify-start justify-start
          "
        >
          <div>
            <v-tooltip v-if="item.endpoint.email" color="#424242" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  class="mr-4"
                  color="#424242"
                  light
                  small
                  outlined
                  v-bind="attrs"
                  v-on="on"
                >
                  Blob
                </v-chip>
              </template>

              {{ item.endpoint.blob }}
            </v-tooltip>

            <v-tooltip v-if="item.endpoint.email" color="#424242" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  class="mr-4"
                  color="#424242"
                  light
                  small
                  outlined
                  v-bind="attrs"
                  v-on="on"
                >
                  Email
                </v-chip>
              </template>

              {{ item.endpoint.email }}
            </v-tooltip>

            <v-tooltip v-if="item.endpoint.email" color="#424242" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  class="mr-4"
                  color="#424242"
                  light
                  small
                  outlined
                  v-bind="attrs"
                  v-on="on"
                >
                  SFTP
                </v-chip>
              </template>

              {{ item.endpoint.sftp }}
            </v-tooltip>

            <v-tooltip v-if="item.endpoint.web" color="#424242" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  class="mr-4"
                  color="#424242"
                  light
                  small
                  outlined
                  v-bind="attrs"
                  v-on="on"
                >
                  Web
                </v-chip>
              </template>

              {{ item.endpoint.web }}
            </v-tooltip>
          </div>

          <div class="flex pl-6">
            <v-progress-linear
              v-if="hasIncomingFiles(item)"
              :color="isOverdue(item) ? 'orange' : 'blue'"
              indeterminate
            ></v-progress-linear>
            <v-progress-linear
              v-else-if="hasActiveJobs(item) && !isOverdue(item)"
              :color="isOverdue(item) ? 'orange' : 'blue'"
              stream
              buffer-value="0"
            ></v-progress-linear>
          </div>
        </div>
      </template>

      <template v-slot:item.time="{ item }">
        <v-tooltip
          v-if="hasActiveJobs(item) && !isOverdue(item)"
          color="blue"
          bottom
        >
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              color="blue"
              text-color="white"
              light
              small
              v-bind="attrs"
              v-on="on"
            >
              <v-progress-circular
                class="mr-4"
                left
                indeterminate
                color="white"
                :width="2"
                :size="12"
              ></v-progress-circular>

              Syncing
            </v-chip>
          </template>

          The tube is currenting fetching data. Start time:
          {{ item.time | date }}.
        </v-tooltip>

        <v-tooltip v-else-if="noScheduleNeeded(item)" color="grey" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              color="grey"
              text-color="white"
              light
              small
              v-bind="attrs"
              v-on="on"
            >
              triggered only
            </v-chip>
          </template>

          This data tube does not need scheduling because it is triggered
          manually, or by another data tube.
        </v-tooltip>

        <v-tooltip v-else-if="hasNoSchedule(item)" color="pink" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              color="pink"
              text-color="white"
              light
              small
              v-bind="attrs"
              v-on="on"
            >
              no sync scheduled
            </v-chip>
          </template>

          Please reschedule if no files have been created within the past hour.
        </v-tooltip>

        <v-tooltip v-else-if="isOverdue(item)" color="orange" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              color="orange"
              text-color="white"
              light
              small
              v-bind="attrs"
              v-on="on"
            >
              <v-icon small left color="white">close</v-icon>
              {{ item.time | timeLeft }}
            </v-chip>
          </template>

          The sync has been taking over an hour since
          {{ item.time | date }}.<br />
          Either the sync is being retried or the processor has been paused.
        </v-tooltip>

        <v-tooltip v-else color="success" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              color="success"
              text-color="white"
              light
              small
              v-bind="attrs"
              v-on="on"
            >
              {{ item.time | timeLeft }}
            </v-chip>
          </template>

          The scheduled sync is on track to run at {{ item.time | date }}.
        </v-tooltip>
      </template>

      <template v-slot:item.schedule="{ item }">
        <reschedule-dialog
          :enabled="
            !item.disableScheduling &&
              !isPending(item) &&
              hasNoSchedule(item) &&
              !!webProcessorsTypes.length
          "
          :status="getQueueStatus(item)"
          :scheduled="[]"
          :client="item.client"
          :dataSource="item.dataSource"
          :showModifiedUntil="showModifiedUntil"
          :resourceType="getResourceType(item)"
          iconColor="pink"
          alertType="pink"
          icon="schedule"
        >
        </reschedule-dialog>
      </template>
      <template v-slot:item.backfill="{ item }">
        <reschedule-dialog
          :enabled="!item.disableScheduling && !!webProcessorsTypes.length"
          :status="getQueueStatus(item)"
          :scheduled="[]"
          :client="item.client"
          :dataSource="item.dataSource"
          :showModifiedUntil="showModifiedUntil"
          :resourceType="getResourceType(item)"
          :disableScheduling="true"
          alertType="blue"
          iconColor="blue"
          icon="history"
        >
        </reschedule-dialog>
      </template>
      <template v-slot:item.pause="{ item }">
        <pause-tube-dialog
          :enabled="hasJobs(item)"
          :client="item.client"
          :dataSource="item.dataSource"
          iconColor="orange"
          icon="pause"
        >
        </pause-tube-dialog>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import moment from 'moment';
import PauseTubeDialog from './PauseTubeDialog.vue';
import RescheduleDialog from '../components/RescheduleDialog.vue';
import { eventBus } from '../main';

export default {
  components: {
    PauseTubeDialog,
    RescheduleDialog,
  },
  props: {
    scheduled: { type: Array },
    customHeaders: { default: () => [] },
    loading: { type: Boolean },
    type: { type: String, default: 'Client' },
    valueKey: { type: String, default: 'client' },
    title: { type: String, default: '' },
    unknownStateActions: { type: Boolean, default: false },
    showModifiedUntil: { type: Boolean, default: false },
    webProcessorsTypes: { type: Array, default: () => [] },
    backFill: { type: Boolean, default: false },
    pause: { type: Boolean, default: false },
  },
  methods: {
    isOverdue(item) {
      return (
        item && item.time && moment().diff(moment(item.time), 'minutes') > 60
      );
    },
    isPending(item) {
      return !!item.pending;
    },
    noScheduleNeeded(item) {
      return item.disableScheduling;
    },
    hasIncomingFiles(item) {
      return item && item.activeRetMessages > 0;
    },
    hasActiveJobs(item) {
      return item && item.activeOrcMessages + item.activeRetMessages > 0;
    },
    hasJobs(item) {
      return item && item.orcMessages + item.retMessages > 0;
    },
    hasNoSchedule(item) {
      return this.unknownStateActions && !item.time;
    },
    getQueueStatus(item) {
      return { status: item.time ? 'Running' : 'Stopped' };
    },
    getResourceType({ client, dataSource }) {
      if (!this.webProcessorsTypes) {
        return '';
      }
      const { types = {} } =
        this.webProcessorsTypes.find(ob => ob.client === client) || {};

      return types[dataSource] || '';
    },
    reloadData() {
      eventBus.$emit('schedule');
    },
  },
  computed: {
    headers() {
      const headers = [
        {
          text: this.type,
          sortable: false,
          value: this.valueKey,
          align: 'left',
          width: '180px',
          sortable: true,
        },
        {
          text: 'Sync Status',
          sortable: true,
          width: '200px',
          value: 'time',
          align: 'left',
        },
        ...this.customHeaders,
        {
          text: '',
          value: 'schedule',
          width: '50px',
          sortable: false,
          align: 'center',
        },
      ];

      if (this.backFill) {
        headers.push({
          text: '',
          value: 'backfill',
          width: '50px',
          sortable: false,
          align: 'center',
        });
      }

      if (this.pause) {
        headers.push({
          text: '',
          value: 'pause',
          width: '50px',
          sortable: false,
          align: 'center',
        });
      }

      return headers;
    },
  },
};
</script>
